import { useCallback, useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AnimatePresence } from "framer-motion";
import api, { fmtErr } from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import Scanning from "../components/app/Scanning";
import LocationDenied from "../components/app/LocationDenied";

import { useLocationAccess } from "../hooks/useLocationAccess";
import { useLiveRoomFeed } from "../hooks/useLiveRoomFeed";
import { useLiveRoomWebSocket } from "../hooks/useLiveRoomWebSocket";
import { usePresenceHeartbeat } from "../hooks/usePresenceHeartbeat";

import VibeComposer from "../components/liveroom/VibeComposer";
import LiveCounter from "../components/liveroom/LiveCounter";
import EmptyFeed from "../components/liveroom/EmptyFeed";
import VibeCard from "../components/liveroom/VibeCard";
import ModeStrip from "../components/liveroom/ModeStrip";
import OpportunityCard from "../components/liveroom/OpportunityCard";
import { useOpportunityCards } from "../hooks/useOpportunityCards";

/**
 * LiveRoom — Vibe Room feed + composer. Post-refactor: this file is a thin
 * orchestrator that wires together 4 hooks and 5 presentational components.
 * All behavior is preserved from the original 418-line version (see git log).
 */
export default function LiveRoomNew() {
  const nav = useNavigate();
  const { t } = useTranslation();
  const { denied, reason, ensurePresence, clearDenied } = useLocationAccess();
  const { feed, setFeed, feedLoaded, setFeedLoaded, refresh } = useLiveRoomFeed({ enabled: !denied });
  useLiveRoomWebSocket({ enabled: !denied && feedLoaded, onVibe: refresh });
  usePresenceHeartbeat({ enabled: !denied });

  const [text, setText] = useState("");
  const [category, setCategory] = useState("CHILL");
  const [busy, setBusy] = useState(false);
  const [scanning, setScanning] = useState(true);
  const [refreshingLoc, setRefreshingLoc] = useState(false);

  // Non-blocking "Updating nearby activity..." banner: appears AFTER the
  // scanning splash + feed load, then auto-hides after ~1s. Coupling to
  // (feedLoaded && !scanning) ensures the toast is actually visible to the
  // user — see iteration_13 for the timing bug this coordination fixes.
  useEffect(() => {
    if (scanning || !feedLoaded) return undefined;
    setRefreshingLoc(true);
    const t = setTimeout(() => setRefreshingLoc(false), 1100);
    return () => clearTimeout(t);
  }, [scanning, feedLoaded]);

  // Initial mount: 2.2s "scanning" splash while presence+feed bootstrap.
  useEffect(() => {
    const t = setTimeout(() => setScanning(false), 2200);
    return () => clearTimeout(t);
  }, []);

  // Bootstrap presence + first feed load once on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const ok = await ensurePresence();
      if (cancelled) return;
      if (!ok) { setFeedLoaded(true); return; }
      await refresh();
    })();
    return () => { cancelled = true; };
  }, [ensurePresence, refresh, setFeedLoaded]);

  const retryLocation = useCallback(async () => {
    clearDenied();
    setScanning(true);
    setFeedLoaded(false);
    setTimeout(() => setScanning(false), 2200);
    const ok = await ensurePresence();
    if (ok) refresh();
    else setFeedLoaded(true);
  }, [clearDenied, ensurePresence, refresh, setFeedLoaded]);

  const drop = async (e) => {
    e.preventDefault();
    if (!text.trim() || busy) return;
    setBusy(true);
    const draft = text.trim();
    setText("");
    try {
      const { data } = await api.post("/vibes", {
        text: draft,
        category,
        // Spot-label input was removed in the UI simplification pass; the
        // backend still accepts a venue_label so we fall back to the resolved
        // venue name (or null in ambient mode) exactly as before.
        venue_label: feed.venue?.name || null,
      });
      // Optimistic insert so the poster sees their own vibe instantly
      if (data?.vibe) {
        setFeed(prev => {
          const already = (prev.vibes || []).some(v => v.id === data.vibe.id);
          if (already) return prev;
          const enriched = { ...data.vibe, is_self: true, i_blinked: false, interest_count: 0, user: { display_name: "you" } };
          return {
            ...prev,
            vibes: [enriched, ...(prev.vibes || [])],
            main: [enriched, ...(prev.main || [])],
          };
        });
      }
      toast.success(t("liveRoom.postedVibe"));
      // Reconcile from server shortly after (server may rank/decorate)
      setTimeout(refresh, 400);
    } catch (err) {
      toast.error(fmtErr(err));
      setText(draft);
    }
    finally { setBusy(false); }
  };

  const dropGif = async (gif) => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/vibes", {
        content_type: "gif",
        text: "",
        category,
        venue_label: feed.venue?.name || null,
        gif: {
          gif_url: gif.gif_url,
          gif_preview_url: gif.gif_preview_url,
          gif_id: gif.gif_id,
          gif_slug: gif.gif_slug,
          gif_width: gif.gif_width,
          gif_height: gif.gif_height,
        },
      });
      if (data?.vibe) {
        setFeed(prev => {
          const already = (prev.vibes || []).some(v => v.id === data.vibe.id);
          if (already) return prev;
          const enriched = { ...data.vibe, is_self: true, i_blinked: false, interest_count: 0, user: { display_name: "you" } };
          return {
            ...prev,
            vibes: [enriched, ...(prev.vibes || [])],
            main: [enriched, ...(prev.main || [])],
          };
        });
      }
      toast.success(t("liveRoom.gifDropped"));
      setTimeout(refresh, 400);
    } catch (err) {
      toast.error(fmtErr(err));
    } finally { setBusy(false); }
  };

  const blink = async (v) => {
    if (v.is_self || v.i_blinked) return;
    try {
      const { data } = await api.post("/interests", {
        to_user: v.user_id,
        venue_id: v.venue_id || "__ambient__",
        vibe_id: v.id,
      });
      if (data.mutual) nav(`/match/${data.match.id}`);
      else { toast.success("BLINK sent"); refresh(); }
    } catch (err) { toast.error(fmtErr(err)); }
  };

  // OPPORTUNITY layer (Layer 1: neutral CAREERS NEARBY deep-link cards).
  // Only actually fetches when the user has toggled OPPORTUNITY mode on
  // (backend also enforces this — belt & suspenders). Hook must live above
  // any early return so React's Hooks order stays stable.
  const opportunityActive = (feed.active_modes || []).includes("opportunity");
  const { cards: opportunityCards } = useOpportunityCards({ active: opportunityActive });

  // Early return: recovery screen takes over the entire viewport so nothing shows behind it.
  if (denied) {
    return <LocationDenied onRetry={retryLocation} reason={reason} />;
  }

  const mainVibes = feed.main || feed.vibes;

  // Interleave opportunity cards into the main feed after every ~4 vibes.
  // We produce a flat items array where each item is either a vibe or an
  // opportunity card — <AnimatePresence> below drives the fade/slide.
  const interleavedItems = (() => {
    if (!opportunityCards.length) return mainVibes.map(v => ({ kind: "vibe", v }));
    const out = [];
    let oppIdx = 0;
    mainVibes.forEach((v, i) => {
      out.push({ kind: "vibe", v });
      // After each 4th vibe, insert one opportunity card until we run out.
      if ((i + 1) % 4 === 0 && oppIdx < opportunityCards.length) {
        out.push({ kind: "opportunity", card: opportunityCards[oppIdx] });
        oppIdx += 1;
      }
    });
    // If the feed is very short (<4 vibes) but we have cards, drop them at the
    // end so they still appear.
    while (oppIdx < opportunityCards.length) {
      out.push({ kind: "opportunity", card: opportunityCards[oppIdx] });
      oppIdx += 1;
    }
    return out;
  })();

  return (
    <div className="min-h-screen bg-black text-white relative">
      {(scanning || !feedLoaded) && <Scanning subtitle="LOOKING FOR NEARBY VIBES" />}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(230,0,0,0.15)_0%,transparent_70%)] pointer-events-none" />
      {refreshingLoc && !scanning && feedLoaded && (
        <div
          className="fixed top-4 left-1/2 -translate-x-1/2 z-40 px-4 py-2 rounded-full bg-black/70 border border-white/10 backdrop-blur-md text-xs font-mono-accent text-zinc-200 tracking-wider shadow-[0_0_20px_rgba(230,0,0,0.15)] bl-fade-in"
          data-testid="liveroom-refreshing-toast"
        >
          <span className="text-[#E60000]">●</span> UPDATING NEARBY ACTIVITY…
        </div>
      )}      <div className="relative max-w-md mx-auto px-5 pt-6 pb-32">
        <div className="flex items-center justify-between mb-6" data-testid="liveroom-header">
          <Link to="/home" className="flex items-center gap-2 text-zinc-300 hover:text-white" data-testid="liveroom-back">
            <ArrowLeft size={18} /> <span>Back</span>
          </Link>
          <span className="font-mono-accent text-zinc-400">{t("liveRoom.title")}</span>
          <span className="w-12" />
        </div>

        <VibeComposer
          text={text} setText={setText}
          category={category} setCategory={setCategory}
          busy={busy} onSubmit={drop} onGifSelect={dropGif}
        />

        <LiveCounter liveCount={feed.live_count} vibesCount={feed.vibes.length} />

        <div className="mt-6">
          <span className="font-mono-accent text-zinc-400">{t("liveRoom.liveFeed")}</span>
        </div>

        {/* Mode section strips — only shown when active */}
        {feed.sections?.perks?.length > 0 && (
          <ModeStrip
            testid="perks-strip"
            label="LOCAL PERKS"
            sublabel="Deals & promos near you"
            accent="#00C46A"
            vibes={feed.sections.perks}
            blink={blink}
          />
        )}
        {feed.sections?.opportunity?.length > 0 && (
          <ModeStrip
            testid="opportunity-strip"
            label="OPPORTUNITY"
            sublabel="Networking · jobs · collabs"
            accent="#3B82F6"
            vibes={feed.sections.opportunity}
            blink={blink}
          />
        )}

        <div className="mt-3 space-y-3" data-testid="vibe-feed">
          {mainVibes.length === 0 && opportunityCards.length === 0 && <EmptyFeed liveCount={feed.live_count} />}
          <AnimatePresence initial={false}>
            {interleavedItems.map(item =>
              item.kind === "vibe"
                ? <VibeCard key={`v-${item.v.id}`} v={item.v} blink={blink} />
                : <OpportunityCard key={`o-${item.card.id}`} card={item.card} />
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
